import { forwardRef } from "react";
import { Trophy, Sprout } from "lucide-react";
import { formatMoney } from "@/lib/currency";

/**
 * GoalShareCard — the 1080×1080 off-screen card that gets rasterised
 * to PNG for social sharing (WhatsApp, Instagram). Extracted from
 * GoalCompleteModal.jsx in iter-51.
 *
 * Kept English-only on purpose — html-to-image doesn't reliably
 * render RTL/complex script fonts inside a rasterised card, and the
 * three social platforms this targets index better on Latin copy.
 */
const GoalShareCard = forwardRef(function GoalShareCard({ goal, currency }, ref) {
  const target = Number(goal.target_amount) || 0;
  return (
    <div
      ref={ref}
      style={{
        width: 1080,
        height: 1080,
        background:
          "radial-gradient(ellipse 900px 600px at 30% 20%, rgba(224,158,80,0.35) 0%, transparent 60%)," +
          "radial-gradient(ellipse 800px 600px at 80% 100%, rgba(67,122,85,0.35) 0%, transparent 55%)," +
          "linear-gradient(180deg, #1A2E22 0%, #12211A 100%)",
        color: "#FDFCFB",
        fontFamily: "var(--font-body)",
        padding: 72,
        display: "flex",
        flexDirection: "column",
        justifyContent: "space-between",
        position: "relative",
        overflow: "hidden",
      }}
      data-testid="goal-share-card"
    >
      {/* Brand mark */}
      <div style={{ display: "flex", alignItems: "center", gap: 14 }}>
        <div style={{
          width: 56, height: 56, borderRadius: 12, background: "#2D5A40",
          display: "flex", alignItems: "center", justifyContent: "center",
        }}>
          <Sprout size={30} color="#FDFCFB" strokeWidth={2.2} />
        </div>
        <div>
          <div style={{ fontSize: 14, letterSpacing: "0.32em", opacity: 0.7, textTransform: "uppercase" }}>Personal</div>
          <div style={{ fontFamily: "var(--font-heading)", fontSize: 22, fontWeight: 500, letterSpacing: "-0.01em" }}>
            Finance Optimizer
          </div>
        </div>
      </div>

      {/* Trophy + big number */}
      <div style={{ display: "flex", flexDirection: "column", alignItems: "flex-start", gap: 24 }}>
        <div style={{
          width: 96, height: 96, borderRadius: 24,
          background: "linear-gradient(135deg, #2D5A40 0%, #E09E50 100%)",
          display: "flex", alignItems: "center", justifyContent: "center",
          boxShadow: "0 24px 60px -20px rgba(0,0,0,0.5)",
        }}>
          <Trophy size={50} color="#FFFFFF" strokeWidth={2} />
        </div>
        <div style={{
          fontSize: 20, letterSpacing: "0.36em", textTransform: "uppercase",
          color: "#E09E50", fontWeight: 500,
        }}>
          Goal complete
        </div>
        <div style={{
          fontFamily: "var(--font-heading)",
          fontSize: 100, fontWeight: 500, lineHeight: 0.95, letterSpacing: "-0.035em",
        }}>
          I saved
        </div>
        <div style={{
          fontFamily: "var(--font-mono)",
          fontSize: 132, fontWeight: 600, lineHeight: 1, letterSpacing: "-0.03em",
          color: "#E09E50",
        }}>
          {formatMoney(target, currency)}
        </div>
        <div style={{
          fontFamily: "var(--font-heading)",
          fontSize: 56, fontWeight: 400, lineHeight: 1.05, letterSpacing: "-0.02em",
          color: "rgba(253,252,251,0.9)", maxWidth: 900,
        }}>
          for {goal.name}
        </div>
      </div>

      {/* Footer */}
      <div style={{
        display: "flex", alignItems: "flex-end", justifyContent: "space-between",
        gap: 24, paddingTop: 24, borderTop: "1px solid rgba(253,252,251,0.14)",
      }}>
        <div>
          <div style={{ fontSize: 14, letterSpacing: "0.28em", textTransform: "uppercase", opacity: 0.55, marginBottom: 6 }}>
            Building the habit
          </div>
          <div style={{ fontSize: 22, opacity: 0.9 }}>One goal at a time.</div>
        </div>
        <div style={{
          fontFamily: "var(--font-mono)",
          fontSize: 16, letterSpacing: "0.08em", opacity: 0.55, textAlign: "right",
        }}>
          {new Date().toLocaleDateString(undefined, { year: "numeric", month: "long", day: "numeric" })}
        </div>
      </div>
    </div>
  );
});

export default GoalShareCard;
